import { Injectable } from '@angular/core';
import { removeIllegalCharactersFromFileName } from '@core/util/documents';
import { BusinessBaseDocx } from '@models/businesses';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessBaseDocxRepo } from '@repos/business-base-docx/business-base-docx.repo';
import { UploadService } from '@services/file/upload.service';
import { AuthState } from '@state/auth';
import { BusinessState } from '@state/business';

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class BaseDocxService {
	@SelectSnapshot(BusinessState.businessId) businessId: string;
	@SelectSnapshot(AuthState.getLoggedInUserId) userId: string;
	constructor(
		private uploadService: UploadService,
		private businessBaseDocxRepo: BusinessBaseDocxRepo,
	) {}

	async updateBaseDocx(baseDocx: BusinessBaseDocx) {
		this.businessBaseDocxRepo.Put(baseDocx.id, {
			...baseDocx,
			lastEditedBy: this.userId,
			lastEditedOn: Date.now(),
		});
	}

	async addOverride(
		file: File,
		baseDocx: BusinessBaseDocx,
	): Promise<BusinessBaseDocx> {
		const path = await this.uploadBaseDocx(file);
		const result = {
			...baseDocx,
			lastEditedBy: this.userId,
			lastEditedOn: Date.now(),
			path,
		} as BusinessBaseDocx;
		await this.businessBaseDocxRepo.Put(baseDocx.id, result);

		return result;
	}

	async addOfficeOverride(
		file: File,
		officeId: string,
		baseDocx: BusinessBaseDocx,
	) {
		const path = await this.uploadBaseDocx(file);
		if (!baseDocx?.overrides?.office) {
			baseDocx.overrides = {
				office: {
					[officeId]: { path },
				},
			};
		} else {
			baseDocx.overrides.office[officeId] = { path };
		}

		await this.businessBaseDocxRepo.Put(baseDocx.id, {
			...baseDocx,
			lastEditedBy: this.userId,
			lastEditedOn: Date.now(),
		});
	}

	async deleteOfficeOverride(officeId: string, baseDocx: BusinessBaseDocx) {
		await this.uploadService.delete(baseDocx.overrides.office[officeId].path);
		delete baseDocx.overrides.office[officeId];

		await this.businessBaseDocxRepo.Put(baseDocx.id, {
			...baseDocx,
			lastEditedBy: this.userId,
			lastEditedOn: Date.now(),
		});
	}

	async uploadBaseDocx(file: File): Promise<string> {
		const fileName = removeIllegalCharactersFromFileName(
			file.name.split('.')[0],
		).replace(/\s/g, '_');
		const path = `word-documents/${this.businessId}/parts/baseDocx/${fileName}.docx`;

		const result = await this.uploadService
			.uploadFirestore(path, file)
			.toPromise();

		return path;
	}
}
